import React, { useEffect, useState } from 'react';
import { IMAGE_PATHS } from 'constants/imagePaths';
import { Stack, Image, Form } from 'react-bootstrap';
import { useGetChatUsersQuery } from 'services';
import useChatStore, {
	setChatListKeyword,
	setChatUser,
	setNewMessageAlert,
	setRoomId,
	setUserList,
} from 'store/chat';
import { getCreatedDifferenceInBestUnit } from 'utils';
import { S3_URL } from 'constants/index';
import { useNavigate, useParams } from 'react-router-dom';
import './styles.scss';
import useDebounce from 'hooks/useDebounce';
import useTranslate from 'hooks/useTranslate';
import { seenMessage } from 'store/socket';

const ChatList: React.FC = () => {
	const { translate } = useTranslate();
	const navigate = useNavigate();
	const { userList, chatListKeyword }: Record<string, any> = useChatStore((state) => state || []);
	const {
		data: userListArr = [],
		isPending,
		refetch,
	} = useGetChatUsersQuery(`keyword=${chatListKeyword}`);
	const { receiverId, roomId } = useParams<string>();

	const [locationQry, setLocationQry] = useState<string | null>(null);
	const debouncedQry = useDebounce(locationQry, 300);

	useEffect(() => {
		if (debouncedQry !== null) {
			refetch();
		}
	}, [debouncedQry]);

	useEffect(() => {
		if (userListArr && !isPending) {
			setUserList(userListArr);
		}
	}, [userListArr, isPending]);

	const onUserSelect = (room_id: number, receiver_id: number) => {
		setNewMessageAlert(false);
		seenMessage(String(room_id));
		navigate(`/chat/${receiver_id}/${room_id}`);
	};

	useEffect(() => {
		if (roomId) {
			seenMessage(String(roomId));
			setChatUser(Number(receiverId));
			setRoomId(Number(roomId));
			refetch();
		}
	}, [roomId]);

	const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = (e.target as HTMLInputElement).value
			.trim()
			.replace(/\s+/g, ' ')
			.replace(/\s+$/, '');

		setChatListKeyword(value || '');
		setLocationQry(value || '');
	};

	return (
		<div className="chat-box-list bg-white">
			<Form.Group className="user-search">
				<div className="position-relative w-100">
					<i className="ri-search-line position-absolute"></i>
					<Form.Control
						type="text"
						name="keyword"
						placeholder={translate('PlaceHolder.searchPeople')}
						className="rounded-pill"
						defaultValue={chatListKeyword || ''}
						onChange={handleOnChange}
						onBlur={(e: any) => {
							e.preventDefault();
						}}
						autoComplete="off"
					/>
				</div>
			</Form.Group>
			<Stack className="chat-box-list-box">
				{userList?.length > 0 &&
					userList?.map((user: any, index: number) => {
						return (
							<div
								key={index}
								className={`chat-profile ${roomId === user?.roomId ? 'active' : ''} ${user?.isBlock || user?.isReported ? 'blocked-chat' : ''}`}
								onClick={() => onUserSelect(user?.roomId, user?.chatUserId)}
							>
								<div className="d-flex gap-2">
									<div className="chat-profile-img position-relative">
										<Image
											src={
												user?.profilePicture
													? S3_URL + user.profilePicture
													: IMAGE_PATHS.UserDefault
											}
											alt="profile"
											className="w-100 h-100 object-fit-cover rounded-circle"
											loading="lazy"
										/>
										{/* <span className="active-sign position-absolute"></span> */}
									</div>
									<div className="chat-profile-content flex-1">
										<div className="d-flex align-items-center justify-content-between mb-1 gap-2 gap-md-3">
											<div className="d-flex align-items-center gap-2">
												<h6 className="fw-bold mb-0">{user?.name}</h6>{' '}
											</div>
											{user?.time && (
												<p className="chat-time mb-0">
													{getCreatedDifferenceInBestUnit(user.time)}
												</p>
											)}
											{user?.unreadCount > 0 && <span className="msg-count"></span>}
										</div>
										{user?.messageType !== 'text' ? (
											<div className="file-detail d-flex align-items-center gap-1">
												{user?.messageType === 'audio' && (
													<>
														<i className="ri-music-2-fill"></i>
														{translate('Chat.audio')}
													</>
												)}
												{user?.messageType === 'video' && (
													<>
														<i className="ri-file-video-fill"></i> {translate('Chat.video')}
													</>
												)}
												{user?.messageType === 'image' && (
													<>
														<i className="ri-image-2-fill"></i> {translate('Chat.image')}
													</>
												)}
												{user?.messageType === 'doc' && (
													<>
														<i className="ri-file-fill"></i> {translate('Chat.doc')}
													</>
												)}
											</div>
										) : (
											<p className="chat-msg mb-0">{user?.message}</p>
										)}
									</div>
								</div>
							</div>
						);
					})}
			</Stack>
		</div>
	);
};

export default ChatList;
